///
/// Copyright 2024-2026 Tesenso AG
///

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { defaultHttpOptionsFromConfig, RequestConfig } from './http-utils';

export interface SwitchableCustomer {
  id: { id: string; entityType: string };
  name: string;
  home: boolean;
}

export interface SwitchCustomerResponse {
  token: string;
  refreshToken: string;
}

@Injectable({
  providedIn: 'root'
})
export class CustomerSwitcherService {

  private static readonly ACTIVE_CUSTOMER_KEY = 'active_customer_id';

  constructor(
    private http: HttpClient
  ) {}

  public getAccessibleCustomers(config?: RequestConfig): Observable<SwitchableCustomer[]> {
    return this.http.get<SwitchableCustomer[]>(
      '/api/tesenso/auth/accessibleCustomers',
      defaultHttpOptionsFromConfig(config)
    );
  }

  public switchCustomer(customerId: string, config?: RequestConfig): Observable<SwitchCustomerResponse> {
    return this.http.post<SwitchCustomerResponse>(
      `/api/tesenso/auth/switchCustomer/${customerId}`,
      {},
      defaultHttpOptionsFromConfig(config)
    );
  }

  public getActiveCustomerId(): string | null {
    return localStorage.getItem(CustomerSwitcherService.ACTIVE_CUSTOMER_KEY);
  }

  public setActiveCustomerId(customerId: string): void {
    localStorage.setItem(CustomerSwitcherService.ACTIVE_CUSTOMER_KEY, customerId);
  }

  public clearActiveCustomerId(): void {
    localStorage.removeItem(CustomerSwitcherService.ACTIVE_CUSTOMER_KEY);
  }

  // --- Admin endpoints ---

  /**
   * Make `customerId` the user's home customer (owner). Backed by the generic
   * ownership endpoint — for a USER this re-homes the account to the given
   * customer and moves its role-default groups. TENANT_ADMIN only.
   */
  public setHomeCustomer(userId: string, customerId: string, config?: RequestConfig): Observable<void> {
    return this.http.post<void>(
      `/api/tesenso/owner/CUSTOMER/${customerId}/USER/${userId}`,
      null,
      defaultHttpOptionsFromConfig(config)
    );
  }

  public grantAccess(userId: string, customerId: string, config?: RequestConfig): Observable<void> {
    return this.http.post<void>(
      `/api/tesenso/admin/userCustomerAccess/${userId}/${customerId}`,
      {},
      defaultHttpOptionsFromConfig(config)
    );
  }

  public revokeAccess(userId: string, customerId: string, config?: RequestConfig): Observable<void> {
    return this.http.delete<void>(
      `/api/tesenso/admin/userCustomerAccess/${userId}/${customerId}`,
      defaultHttpOptionsFromConfig(config)
    );
  }

  public getUserAccessibleCustomers(userId: string, config?: RequestConfig): Observable<SwitchableCustomer[]> {
    return this.http.get<SwitchableCustomer[]>(
      `/api/tesenso/admin/userCustomerAccess/${userId}`,
      defaultHttpOptionsFromConfig(config)
    );
  }

  public inviteUserToCustomer(userId: string, config?: RequestConfig): Observable<void> {
    return this.http.post<void>(
      `/api/tesenso/customer/inviteUser/${userId}`,
      {},
      defaultHttpOptionsFromConfig(config)
    );
  }
}
